/*
  C Home Rhythm Banner: background video behaviour (sections/c-home-rhythm-banner.liquid).
  Designer note on the Figma frame: "autoplay on scroll. muted at first".

  - The video loads nothing until the section gets close to the screen, plays muted while at
    least a quarter of it is visible, and pauses when it leaves.
  - Sound button: the only way to unmute. Pause button: stops the loop for good (WCAG 2.2.2);
    a visitor who paused is never restarted by scrolling.
  - prefers-reduced-motion: the still stays; the video only plays if the visitor presses play
    or turns the sound on.
  - A custom element, so it also works when the theme editor re-renders the section.
*/
(() => {
  if (customElements.get('c-home-rhythm-banner')) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class CHomeRhythmBanner extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('.c-home-rhythm-banner__video');
      if (!this.video) return;

      this.soundButton = this.querySelector('[data-rb-sound]');
      this.playButton = this.querySelector('[data-rb-play]');
      this.inView = false;
      this.userPaused = false;
      this.userStarted = false;

      this.onMotionChange = () => this.update();
      this.onPlaying = () => {
        this.classList.add('is-started');
        this.sync();
      };
      this.onSync = () => this.sync();
      this.onSoundClick = () => this.toggleSound();
      this.onPlayClick = () => this.togglePlay();

      this.video.addEventListener('playing', this.onPlaying);
      this.video.addEventListener('pause', this.onSync);
      this.video.addEventListener('volumechange', this.onSync);
      if (this.soundButton) this.soundButton.addEventListener('click', this.onSoundClick);
      if (this.playButton) this.playButton.addEventListener('click', this.onPlayClick);
      if (typeof reduceMotion.addEventListener === 'function') {
        reduceMotion.addEventListener('change', this.onMotionChange);
      }

      if ('IntersectionObserver' in window) {
        // Fetch the file a little before the section arrives, so it is ready when it shows.
        this.preloadObserver = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              this.preloadObserver.disconnect();
              if (this.wantsMotion()) this.load();
            }
          },
          { rootMargin: '0px 0px 50% 0px' }
        );
        this.preloadObserver.observe(this);

        this.viewObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.inView = entry.isIntersecting;
            });
            this.update();
          },
          { threshold: 0.25 }
        );
        this.viewObserver.observe(this);
      } else {
        this.inView = true;
        this.update();
      }

      this.sync();
    }

    disconnectedCallback() {
      if (this.preloadObserver) this.preloadObserver.disconnect();
      if (this.viewObserver) this.viewObserver.disconnect();
      if (typeof reduceMotion.removeEventListener === 'function' && this.onMotionChange) {
        reduceMotion.removeEventListener('change', this.onMotionChange);
      }
      if (this.video) this.video.pause();
    }

    wantsMotion() {
      return !this.userPaused && (!reduceMotion.matches || this.userStarted);
    }

    /*
      Sized sources are the derived 480p/720p MP4s; data-fallback ones (the original 1080p MP4, then
      HLS) are only used if those fail. Picks the smallest sized source that covers the rendered,
      cover-cropped height (DPR capped at 1.5: this is a darkened background loop).
    */
    load() {
      if (this.loaded) return;
      const all = Array.from(this.video.querySelectorAll('source[data-src]'))
        .map((source) => ({
          url: source.dataset.src,
          height: Number(source.dataset.height) || 0,
          fallback: source.hasAttribute('data-fallback'),
        }))
        .filter((source) => source.url);
      if (!all.length) return;

      const sized = all.filter((source) => !source.fallback).sort((a, b) => a.height - b.height);
      const box = this.video.getBoundingClientRect();
      const ratio = Number(this.video.dataset.aspect) || 16 / 9;
      const coverHeight = Math.max(box.height, box.width / ratio);
      const needed = coverHeight * Math.min(window.devicePixelRatio || 1, 1.5) * 0.8;
      const pick = sized.find((source) => source.height >= needed) || sized[sized.length - 1];

      this.queue = pick
        ? [pick, ...sized.filter((source) => source.height > pick.height), ...all.filter((source) => source.fallback)]
        : all.filter((source) => source.fallback);
      this.loaded = true;
      this.onError = () => {
        if (this.next() && this.inView && this.wantsMotion()) this.play();
      };
      this.video.addEventListener('error', this.onError);
      this.next();
    }

    next() {
      const source = this.queue && this.queue.shift();
      if (!source) return false;
      this.video.preload = 'auto';
      this.video.src = source.url;
      return true;
    }

    play() {
      this.load();
      const attempt = this.video.play();
      // Autoplay can still be refused (e.g. low power mode): the still stays and the play button offers it.
      if (attempt && typeof attempt.catch === 'function') attempt.catch(() => this.sync());
    }

    update() {
      if (this.inView && this.wantsMotion()) {
        this.play();
      } else if (!this.video.paused) {
        this.video.pause();
      }
    }

    togglePlay() {
      if (this.video.paused) {
        this.userPaused = false;
        this.userStarted = true;
        this.play();
      } else {
        this.userPaused = true;
        this.video.pause();
      }
      this.sync();
    }

    toggleSound() {
      this.video.muted = !this.video.muted;
      if (!this.video.muted) {
        if (this.video.volume === 0) this.video.volume = 1;
        // Turning the sound on is an explicit request to watch: it also starts playback,
        // even for reduced motion or after a pause.
        this.userPaused = false;
        this.userStarted = true;
        this.play();
      }
      this.sync();
    }

    sync() {
      if (this.soundButton) {
        const soundOn = !this.video.muted && this.video.volume > 0;
        this.soundButton.classList.toggle('is-on', soundOn);
        this.setLabel(this.soundButton, soundOn ? 'labelOn' : 'labelOff');
      }
      if (this.playButton) {
        const playing = !this.video.paused;
        this.playButton.classList.toggle('is-on', playing);
        this.setLabel(this.playButton, playing ? 'labelOn' : 'labelOff');
      }
    }

    setLabel(button, key) {
      const label = button.dataset[key];
      if (!label) return;
      button.setAttribute('aria-label', label);
      const text = button.querySelector('.c-home-rhythm-banner__sr');
      if (text) text.textContent = label;
    }
  }

  customElements.define('c-home-rhythm-banner', CHomeRhythmBanner);
})();
